import { useState, useRef, useEffect, useCallback } from "react";

interface ImageCropperModalProps {
  imageUrl: string;
  targetRatio: number; // ancho / alto
  onCrop: (croppedBlob: Blob) => void;
  onCancel: () => void;
}

export default function ImageCropperModal({
  imageUrl,
  targetRatio,
  onCrop,
  onCancel,
}: ImageCropperModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });

  // Frame size (fixed in center of container)
  const [frameSize, setFrameSize] = useState({ w: 0, h: 0 });

  // Image transform: scale + translate (pan)
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const isPanningRef = useRef(false);
  const panStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  // Load image
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imgRef.current = img;
      setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
      // Reset transform when new image loads
      setScale(1);
      setPan({ x: 0, y: 0 });
    };
    img.src = imageUrl;
  }, [imageUrl]);

  // Measure container
  useEffect(() => {
    const measure = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setContainerSize({ w: rect.width, h: rect.height });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Compute frame size to fit in container with target ratio
  useEffect(() => {
    const { w: cw, h: ch } = containerSize;
    if (cw === 0 || ch === 0) return;

    // Frame fills as much as possible while maintaining ratio
    // Leave padding so image can be panned around
    const padding = 40;
    const availW = cw - padding * 2;
    const availH = ch - padding * 2;

    let fw = availW;
    let fh = fw / targetRatio;
    if (fh > availH) {
      fh = availH;
      fw = fh * targetRatio;
    }
    setFrameSize({ w: fw, h: fh });
  }, [containerSize, targetRatio]);

  // Auto-fit image to frame on first load
  useEffect(() => {
    if (naturalSize.w === 0 || frameSize.w === 0) return;
    if (scale !== 1 || pan.x !== 0 || pan.y !== 0) return; // Only on fresh load

    const imgRatio = naturalSize.w / naturalSize.h;
    const frameRatio = frameSize.w / frameSize.h;

    // Calculate base scale so image fits ENTIRELY inside the frame (object-fit: contain)
    let baseScale = 1;
    if (imgRatio > frameRatio) {
      // Image is wider relative to frame -> fit width
      baseScale = frameSize.w / naturalSize.w;
    } else {
      // Image is taller relative to frame -> fit height
      baseScale = frameSize.h / naturalSize.h;
    }

    // Ensure image isn't displayed smaller than a reasonable minimum zoom
    const minScale = 0.05;
    baseScale = Math.max(minScale, baseScale);

    setScale(baseScale);

    // Center the image
    const imgDisplayW = naturalSize.w * baseScale;
    const imgDisplayH = naturalSize.h * baseScale;
    const cx = (containerSize.w - imgDisplayW) / 2;
    const cy = (containerSize.h - imgDisplayH) / 2;
    setPan({ x: cx, y: cy });
  }, [naturalSize, frameSize, containerSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pan handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsPanning(true);
    isPanningRef.current = true;
    panStart.current = {
      x: e.clientX,
      y: e.clientY,
      panX: pan.x,
      panY: pan.y,
    };
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isPanningRef.current) return;
      const dx = e.clientX - panStart.current.x;
      const dy = e.clientY - panStart.current.y;
      setPan({
        x: panStart.current.panX + dx,
        y: panStart.current.panY + dy,
      });
    },
    []
  );

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
    isPanningRef.current = false;
  }, []);

  useEffect(() => {
    if (isPanning) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isPanning, handleMouseMove, handleMouseUp]);

  // Touch pan support
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      setIsPanning(true);
      isPanningRef.current = true;
      panStart.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
        panX: pan.x,
        panY: pan.y,
      };
    }
  };

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isPanningRef.current || e.touches.length !== 1) return;
      const dx = e.touches[0].clientX - panStart.current.x;
      const dy = e.touches[0].clientY - panStart.current.y;
      setPan({
        x: panStart.current.panX + dx,
        y: panStart.current.panY + dy,
      });
    },
    []
  );

  const handleTouchEnd = useCallback(() => {
    setIsPanning(false);
    isPanningRef.current = false;
  }, []);

  useEffect(() => {
    if (isPanning) {
      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      window.addEventListener("touchend", handleTouchEnd);
      return () => {
        window.removeEventListener("touchmove", handleTouchMove);
        window.removeEventListener("touchend", handleTouchEnd);
      };
    }
  }, [isPanning, handleTouchMove, handleTouchEnd]);

  // Wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.05 : 0.05;
    setScale((prev) => Math.max(0.2, Math.min(5, prev + delta)));
  };

  const performCrop = () => {
    if (!canvasRef.current || !imgRef.current || naturalSize.w === 0 || frameSize.w === 0) return;

    const img = imgRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Frame position in container
    const frameX = (containerSize.w - frameSize.w) / 2;
    const frameY = (containerSize.h - frameSize.h) / 2;

    // Image display dimensions at current scale
    const imgDisplayW = naturalSize.w * scale;
    const imgDisplayH = naturalSize.h * scale;

    // Image top-left in container
    const imgX = pan.x;
    const imgY = pan.y;

    // Intersection: what part of the image is under the frame?
    const overlapX = Math.max(imgX, frameX);
    const overlapY = Math.max(imgY, frameY);
    const overlapRight = Math.min(imgX + imgDisplayW, frameX + frameSize.w);
    const overlapBottom = Math.min(imgY + imgDisplayH, frameY + frameSize.h);

    const overlapW = Math.max(0, overlapRight - overlapX);
    const overlapH = Math.max(0, overlapBottom - overlapY);

    if (overlapW <= 0 || overlapH <= 0) {
      // No overlap, just use center of image
      const targetW = Math.min(naturalSize.w, 1200);
      const targetH = targetW / targetRatio;
      canvas.width = targetW;
      canvas.height = targetH;
      const srcX = (naturalSize.w - targetW) / 2;
      const srcY = (naturalSize.h - targetH) / 2;
      ctx.drawImage(img, srcX, srcY, targetW, targetH, 0, 0, targetW, targetH);
    } else {
      // Convert display pixels to natural pixels
      const srcX = (overlapX - imgX) / scale;
      const srcY = (overlapY - imgY) / scale;
      const srcW = overlapW / scale;
      const srcH = overlapH / scale;

      canvas.width = srcW;
      canvas.height = srcH;
      ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);
    }

    canvas.toBlob(
      (blob) => {
        if (blob) onCrop(blob);
      },
      "image/jpeg",
      0.9
    );
  };

  const zoomIn = () => setScale((s) => Math.min(5, s + 0.15));
  const zoomOut = () => setScale((s) => Math.max(0.2, s - 0.15));
  const resetView = () => {
    if (naturalSize.w === 0 || frameSize.w === 0) return;
    const imgRatio = naturalSize.w / naturalSize.h;
    const frameRatio = frameSize.w / frameSize.h;
    let baseScale = 1;
    if (imgRatio > frameRatio) {
      baseScale = frameSize.w / naturalSize.w;
    } else {
      baseScale = frameSize.h / naturalSize.h;
    }
    const minScale = 0.05;
    baseScale = Math.max(minScale, baseScale);
    setScale(baseScale);
    const imgDisplayW = naturalSize.w * baseScale;
    const imgDisplayH = naturalSize.h * baseScale;
    setPan({
      x: (containerSize.w - imgDisplayW) / 2,
      y: (containerSize.h - imgDisplayH) / 2,
    });
  };

  const imgDisplayW = naturalSize.w * scale;
  const imgDisplayH = naturalSize.h * scale;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3 sm:p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="bg-white rounded-2xl w-full max-w-4xl h-[90vh] sm:h-[85vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="px-5 sm:px-6 py-3 sm:py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h3
              className="text-[#0B1F3A] font-bold text-sm"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              <i className="ri-crop-line mr-1.5" />
              Ajusta la imagen
            </h3>
            <p className="text-slate-400 text-xs mt-0.5">
              Arrastra para mover · Rueda del ratón o slider para zoom · El marco blanco muestra el resultado final
            </p>
          </div>
          <button
            onClick={onCancel}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-5 sm:px-6 py-2 border-b border-slate-100 flex items-center gap-3 flex-shrink-0 bg-slate-50/50 flex-wrap">
          <button
            onClick={zoomOut}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-slate-200 hover:bg-slate-100 text-slate-600 transition-colors cursor-pointer"
            title="Alejar"
          >
            <i className="ri-zoom-out-line text-sm" />
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-[120px]">
            <input
              type="range"
              min="20"
              max="500"
              value={Math.round(scale * 100)}
              onChange={(e) => setScale(Number(e.target.value) / 100)}
              className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#0B1F3A]"
            />
            <span className="text-xs text-slate-500 font-mono w-12 text-right">
              {Math.round(scale * 100)}%
            </span>
          </div>
          <button
            onClick={zoomIn}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-slate-200 hover:bg-slate-100 text-slate-600 transition-colors cursor-pointer"
            title="Acercar"
          >
            <i className="ri-zoom-in-line text-sm" />
          </button>
          <div className="w-px h-6 bg-slate-200 mx-1" />
          <button
            onClick={resetView}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:bg-slate-100 text-slate-600 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-restart-line" />
            Reiniciar
          </button>
        </div>

        {/* Canvas area */}
        <div
          ref={containerRef}
          className="flex-1 relative overflow-hidden bg-slate-900 select-none cursor-grab active:cursor-grabbing"
          onMouseDown={handleMouseDown}
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
        >
          {naturalSize.w > 0 && (
            <>
              {/* Image layer */}
              <img
                src={imageUrl}
                alt="crop"
                draggable={false}
                className="absolute top-0 left-0 will-change-transform"
                style={{
                  width: imgDisplayW,
                  height: imgDisplayH,
                  transform: `translate(${pan.x}px, ${pan.y}px)`,
                  imageRendering: "auto",
                }}
              />

              {/* Overlay with frame cutout */}
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none"
              >
                <defs>
                  <mask id="crop-frame-mask">
                    <rect width="100%" height="100%" fill="white" />
                    <rect
                      x={(containerSize.w - frameSize.w) / 2}
                      y={(containerSize.h - frameSize.h) / 2}
                      width={frameSize.w}
                      height={frameSize.h}
                      rx="4"
                      fill="black"
                    />
                  </mask>
                </defs>
                <rect
                  width="100%"
                  height="100%"
                  fill="rgba(0,0,0,0.65)"
                  mask="url(#crop-frame-mask)"
                />
                {/* Frame border */}
                <rect
                  x={(containerSize.w - frameSize.w) / 2}
                  y={(containerSize.h - frameSize.h) / 2}
                  width={frameSize.w}
                  height={frameSize.h}
                  rx="4"
                  fill="none"
                  stroke="white"
                  strokeWidth="2"
                />
                {/* Rule of thirds */}
                <g
                  transform={`translate(${(containerSize.w - frameSize.w) / 2}, ${(containerSize.h - frameSize.h) / 2})`}
                >
                  <line
                    x1={frameSize.w / 3}
                    y1={0}
                    x2={frameSize.w / 3}
                    y2={frameSize.h}
                    stroke="rgba(255,255,255,0.4)"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                  />
                  <line
                    x1={(frameSize.w * 2) / 3}
                    y1={0}
                    x2={(frameSize.w * 2) / 3}
                    y2={frameSize.h}
                    stroke="rgba(255,255,255,0.4)"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                  />
                  <line
                    x1={0}
                    y1={frameSize.h / 3}
                    x2={frameSize.w}
                    y2={frameSize.h / 3}
                    stroke="rgba(255,255,255,0.4)"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                  />
                  <line
                    x1={0}
                    y1={(frameSize.h * 2) / 3}
                    x2={frameSize.w}
                    y2={(frameSize.h * 2) / 3}
                    stroke="rgba(255,255,255,0.4)"
                    strokeWidth="1"
                    strokeDasharray="4 4"
                  />
                </g>
                {/* Corner markers */}
                {(() => {
                  const fx = (containerSize.w - frameSize.w) / 2;
                  const fy = (containerSize.h - frameSize.h) / 2;
                  const corners = [
                    { x: fx, y: fy, rot: 0 },
                    { x: fx + frameSize.w, y: fy, rot: 90 },
                    { x: fx + frameSize.w, y: fy + frameSize.h, rot: 180 },
                    { x: fx, y: fy + frameSize.h, rot: 270 },
                  ];
                  return corners.map((c, i) => (
                    <g key={i} transform={`translate(${c.x},${c.y}) rotate(${c.rot})`}>
                      <line x1="-12" y1="0" x2="-4" y2="0" stroke="#0B1F3A" strokeWidth="2.5" />
                      <line x1="0" y1="-12" x2="0" y2="-4" stroke="#0B1F3A" strokeWidth="2.5" />
                      <line x1="-12" y1="0" x2="-4" y2="0" stroke="white" strokeWidth="1" />
                      <line x1="0" y1="-12" x2="0" y2="-4" stroke="white" strokeWidth="1" />
                    </g>
                  ));
                })()}
              </svg>

              {/* Frame label */}
              <div
                className="absolute pointer-events-none bg-black/60 text-white text-[10px] font-bold px-2 py-0.5 rounded-full tracking-wider uppercase backdrop-blur-sm"
                style={{
                  left: (containerSize.w - frameSize.w) / 2 + 8,
                  top: (containerSize.h - frameSize.h) / 2 + 8,
                }}
              >
                Resultado final
              </div>
            </>
          )}

          {naturalSize.w === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin text-white/50 text-3xl" />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 sm:px-6 py-3 sm:py-4 border-t border-slate-100 flex items-center justify-between flex-shrink-0 bg-white">
          <div className="text-xs text-slate-400 hidden sm:block">
            {naturalSize.w > 0 && (
              <span>
                Original: {naturalSize.w} × {naturalSize.h} px · Salida: {Math.round(frameSize.w / scale)} × {Math.round(frameSize.h / scale)} px
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 ml-auto">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-slate-200 text-slate-600 text-xs font-semibold rounded-lg hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              onClick={performCrop}
              disabled={naturalSize.w === 0}
              className="px-5 py-2.5 bg-[#0B1F3A] hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-bold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-check-line" /> Aplicar y subir
            </button>
          </div>
        </div>
      </div>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}